import { useState } from "react";
import WorkLogReport from "../../models/WorkLogReport";
import { Popover, makeStyles } from "@material-ui/core";
import TableBasedCrud from "../../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import WorkLog from "../../models/WorkLog";
import DateUtils from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/DateUtils";
import PagedResult from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/core/PagedResult";
import WorkLogService from "../../services/WorkLogService";
import { useTranslation } from "react-i18next";
import NotificationService from "../../external/ensolvers-core-frontend-mui/submodules/fox-typescript/services/NotificationService";
import { EnsolversOpsUser } from "../../models/EnsolversOpsUser";
import UserService from "../../services/UserService";
import Holiday from "../../models/Holiday";
import HolidayService from "../../services/HolidayService";
import { Country } from "../../utils/Countries";
import { WorklogModalCreation } from "../Worklog/WorklogModalCreation";
import AssignationService from "../../services/AssignationService";
import PositionService from "../../services/PositionService";
import Position from "../../models/Position";
import Assignation from "../../models/Assignation";
import OpsDateUtils from "../../utils/OpsDateUtils";

const FIRST_PAGE = 0,
  PAGE_SIZE = 7;

const useStyles = makeStyles({
  tableContainer: {
    "& .MuiTableCell-root": {
      padding: "0.5rem",
    },
  },
},
{ index: 1 },
);

interface Props {
  report: WorkLogReport;
  date: Date;
  day?: number;
  anchorEl: HTMLButtonElement | null;
  onClose: () => void;
  onWorkLogUpdate: (worLog: WorkLog, prevInternalHours: number) => void;
}

function WorkLogPopup({ report, date, day, anchorEl, onClose, onWorkLogUpdate }: Props) {
  const { t } = useTranslation();

  const classes = useStyles();
  const [user, setUser] = useState({} as EnsolversOpsUser);
  const [holidays, setHolidays] = useState([] as Holiday[]);
  const [position, setPosition] = useState({} as Position);
  const [openForm, setOpenForm] = useState(false);
  const [pagedWorkLogs, setPagedWorkLogs] = useState<PagedResult<WorkLog>>(PagedResult.empty);
  const [fetchedWorkLogs, setFecthedWorkLogs] = useState(false);
  const [workLogForEdit, setWorkLogForEdit] = useState<WorkLog | undefined>(undefined);

  const handleError = (error: unknown, errorMessage: string) => {
    NotificationService.error(errorMessage);
    throw error;
  };

  /**
   * Performs asynchronous data fetching and error handling.
   *
   * @param fetchFunction A function that returns a Promise of type T.
   * @param errorMessage A message to display in case of an error.
   * @returns A Promise of type T, representing the fetched data.
   */
  async function handleFetchData<T>(fetchFunction: () => Promise<T>, errorMessage: string): Promise<T> {
    try {
      return await fetchFunction();
    } catch (error) {
      return handleError(error, errorMessage);
    }
  }

  const fetchPagedWorklogs = async (): Promise<PagedResult<WorkLog>> => {
    const fetchPagedWorklogsData = async () => {
      const response = await WorkLogService.searchAll(
        FIRST_PAGE,
        PAGE_SIZE,
        report.assignationExternalId,
        date!.getFullYear(),
        date!.getMonth(),
        report.coreUserExternalId,
        day
      );
      return response.getContent();
    };

    return handleFetchData(fetchPagedWorklogsData, "Error fetching work logs");
  };

  /**
   * Retrieves data from work logs (WorkLog).
   * If the data has been previously retrieved, it returns the stored information.
   * Otherwise, it makes a request to fetchPagedWorklogs, stores the data, and returns it.
   *
   * @returns A promise that resolves to a PagedResult<WorkLog> object containing work logs.
   */
  const handleFetchWorkLogs = async (): Promise<PagedResult<WorkLog>> => {
    if (!fetchedWorkLogs) {
      setFecthedWorkLogs(true);
      setPagedWorkLogs(await fetchPagedWorklogs());
    }
    return pagedWorkLogs;
  };

  const fetchHolidays = async (): Promise<Holiday[]> => {
    const fetchHolidaysData = async () => {
      const month = date!.getMonth();
      const country = report.country || Country.ARGENTINA;
      return HolidayService.findHolidaysOfMonthByCountry(month, country).then((result) => result.getContent());
    };

    return handleFetchData(fetchHolidaysData, "Error fetching Holidays");
  };

  const fetchUser = async (): Promise<EnsolversOpsUser> => {
    const fetchUserData = async () => {
      return UserService.findByCoreUserId(report.coreUserExternalId).then((result) => result.getContentOrThrowError());
    };

    return handleFetchData(fetchUserData, "Error getting user information");
  };

  const fetchAssignation = async (): Promise<Assignation> => {
    const fetchAssignationData = async () => {
      return AssignationService.getByExternalId(report.assignationExternalId).then((result) => result.getContent());
    };

    return handleFetchData(fetchAssignationData, "Error getting user assignation");
  };

  const fetchPosition = async (assignation: Assignation): Promise<Position> => {
    const fetchPositionData = async () => {
      const externalId = assignation.position.externalId!;
      return PositionService.getByExternalId(externalId).then((result) => result.getContent());
    };

    return handleFetchData(fetchPositionData, "Error getting user position");
  };

  const handleOpenForm = async (worklog: WorkLog): Promise<void> => {
    try {
      const [holidaysRes, userRes, assignationRes] = await Promise.all([fetchHolidays(), fetchUser(), fetchAssignation()]);
      setHolidays(holidaysRes);
      setUser(userRes);

      worklog.assignationName = assignationRes.externalId!;
      worklog.assignationExternalId = assignationRes.externalId!;

      setPosition(await fetchPosition(assignationRes));

      setWorkLogForEdit(worklog);
      setOpenForm(true);
    } catch (error) {
      console.error("[Handle Open Form] An error occurred:", error);
    }
  };

  const handleUpdateWorkLog = async () => {
    const prevHours: number = workLogForEdit!.hours;
    const updatedWorkLog: WorkLog = (await WorkLogService.getByExternalId(workLogForEdit!.externalId!)).getContent();
    const updatedReport: WorkLog[] = pagedWorkLogs!.content.map((workLog) =>
      workLog.externalId === updatedWorkLog.externalId ? updatedWorkLog : workLog
    );

    setPagedWorkLogs(PagedResult.fromArray(updatedReport));
    setWorkLogForEdit(undefined);

    onWorkLogUpdate(updatedWorkLog, prevHours);
  };

  return (
    <>
      <Popover
        className={classes.tableContainer}
        open={Boolean(anchorEl)}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <TableBasedCrud<WorkLog>
          highlightOnHover={true}
          elementName={"Weekly Worklog"}
          denseTable={true}
          disableAutoFetch
          onDataFetch={handleFetchWorkLogs}
          renderDialogContent={() => <></>}
          hideActions={true}
          enablePagination={false}
          tableColumns={[
            {
              header: t("Date"),
              content: (instance) => (
                <span id={`day-${instance.shortDay.getDate ? instance.shortDay.getDate() : new Date(instance.shortDay).getDate()}`}>
                  {OpsDateUtils.formatUTCAsDDMMM(instance?.shortDay)}
                </span>
              ),
            },
            {
              header: t("Hours"),
              content: (instance) => <>{instance?.hours > 0 ? instance.hours : ""}</>,
            },
            {
              header: t("Description"),
              content: (instance) => <>{instance.description}</>,
            },
            {
              header: t("Internal Hours"),
              content: (instance) => <>{instance?.internalHours > 0 ? instance.internalHours : ""}</>,
            },
          ]}
          onElementClick={handleOpenForm}
        />
      </Popover>
      {!!workLogForEdit && (
        <WorklogModalCreation
          open={openForm}
          setOpen={setOpenForm}
          isEditMode={true}
          setIsEditMode={() => {}}
          workLog={workLogForEdit}
          setWorkLog={setWorkLogForEdit}
          holidays={holidays}
          paramUserExternalId={report.coreUserExternalId}
          user={user!}
          position={position}
          setFetchUpdate={handleUpdateWorkLog}
        />
      )}
    </>
  );
}

export default WorkLogPopup;
